import IconButton from "@mui/material/IconButton";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { TextButton } from "src/components/Default/LekUI/Button";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import { EllipsisText } from "src/components/Main/News/EllipsisText";
import { useModals } from "src/components/Modals/context/ModalProvider";
import { ModalsNames } from "src/components/Modals/context/modals";
import Gallery from "src/components/NewComponents/Images/Gallery";
import { ShortLimitedAccess } from "src/components/NewComponents/LimitedAccess";
import ExamsShortViewSkeleton from "src/components/NewComponents/Skeletons/ExamsShortView";
import formatHtmlTag from "src/helpers/formatHtmlTag";
import { hasLimitedAccess } from "src/helpers/getPermissions";
import isMobile from "src/helpers/isMobile";
import { isLekEn } from "src/helpers/services";
import { useGetComments } from "src/hooks/useComments";
import { useFontSize } from "src/hooks/useFontSize";
import ArrowDownIcon from "src/images/Icons/newIcons/arrowDown.svg";
import ArrowNextIcon from "src/images/Icons/newIcons/controllers/arrowNext.svg";
import AdminProfileIcon from "src/images/Icons/newIcons/userProfile/logo.svg";
import ProfileIcon from "src/images/Icons/newIcons/userProfile/profile.svg";

interface CommentTerm {
	termShort: string;
}

interface CommentData {
	id: string;
	comment: string;
	date: string;
	questionId: string;
	images?: { url: string }[];
	user: {
		id: string;
		username: string;
		isStaff: boolean;
	};
	question: {
		question: string;
		terms: CommentTerm[];
	};
}

interface LastCommentProps {
	comment: CommentData;
}

const LastComment = ({ comment }: LastCommentProps) => {
	const { openModal } = useModals();
	const { fontSizeClass, getFontSizeClass } = useFontSize();
	const Logo = comment.user.isStaff ? AdminProfileIcon : ProfileIcon;
	const { t } = useTranslation();

	const renderTerms = () => {
		if (!comment.question.terms.length) {
			return null;
		}

		return comment.question.terms.map((term) => term.termShort).join(", ");
	};

	return (
		<div className="content-box flex-col last_comment">
			<div className="flex gap-16">
				<Tooltip
					title={comment.user.isStaff ? t("tooltips.admin") : ""}
					className="h-fit-content"
				>
					<div className="profile--user-avatar square-48">
						<Logo className="icon-normal" />
					</div>
				</Tooltip>
				<div className="flex-1">
					<p className={`m-0 ${fontSizeClass}`}>
						<b>{comment.user.username}</b> {t("commentedQuestions.userComment")}{" "}
						{renderTerms()}
					</p>
					<span
						className={`text-grey fr-view text-ellipsis-2 italic ${getFontSizeClass(
							-1,
						)}`}
						dangerouslySetInnerHTML={{
							__html: formatHtmlTag(comment.question.question),
						}}
					/>
				</div>
				<Tooltip title={t("tooltips.watchQuestion")} className="h-fit-content">
					<IconButton
						size="small"
						className="menu-icon-button"
						onClick={() =>
							openModal(ModalsNames.QuestionViewModal, {
								questionId: comment.questionId,
								settings: {
									comments: comment.questionId,
								},
							})
						}
						disableRipple
					>
						<ArrowNextIcon className="icon-small" />
					</IconButton>
				</Tooltip>
			</div>
			<div className="flex mt-12 gap-16">
				<div className="square-48 size-14-light text-grey">
					{dayjs(comment.date)
						.locale(isLekEn() ? "en" : "pl")
						.format("DD MMM HH:mm")}
				</div>
				<div className="flex-1 flex-col gap-12 flex">
					<EllipsisText
						fontSizeClassName={fontSizeClass}
						className={`${fontSizeClass} fr-view`}
						html={comment.comment}
					/>
					{comment?.images?.[0]?.url && (
						<div>
							<Gallery
								images={[{ image: comment.images[0].url }]}
								maxImages={isMobile() ? 5 : null}
								onDelete={undefined}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

interface LastCommentsProps {
	isOpen: boolean;
}

export function LastComments({ isOpen }: LastCommentsProps) {
	const { data, hasNextPage, fetchNextPage, isFetching, isFetched } =
		useGetComments(isOpen);
	const fetchedPages = (data as any)?.currentPage ?? 0;
	const { t } = useTranslation();

	const renderContent = () => {
		if (!data?.pages?.length && isFetching) {
			return <ExamsShortViewSkeleton />;
		}

		if (hasLimitedAccess()) {
			return <ShortLimitedAccess />;
		}

		if (!data?.pages?.length && isFetched) {
			return (
				<div>
					<span>{t("defaultsText.emptyCommentsList")}</span>
				</div>
			);
		}

		if (!data?.pages?.length) {
			return null;
		}

		const items = fetchedPages === 1 ? data.pages.slice(0, 2) : data.pages;

		return items.map((item: CommentData) => {
			return (
				<LastComment comment={item} key={item.id} />
			);
		});
	};

	const renderMoreButton = () => {
		if (!hasNextPage) {
			return null;
		}

		return (
			<TextButton
				className="primary-text-btn"
				endIcon={<ArrowDownIcon className="icon-normal" />}
				onClick={() => fetchNextPage()}
				isLoading={isFetching}
			>
				{t("commands.seeMore")}
			</TextButton>
		);
	};

	return (
		<div className="flex flex-col gap-12">
			{renderContent()}
			{hasNextPage && (
				<div className="flex-center-row">{renderMoreButton()}</div>
			)}
		</div>
	);
}
